import {
  Color,
  Combobox,
  DropdownButton,
  Paragraph
} from '@eggplant/pattern-library'
import { GetCustomScriptListRequest } from '@eggplant/api-requests'
import type {
  ButtonShape,
  GetCustomScriptList,
  Options,
  Placement
} from '@eggplant/types'

interface Props {
  hasClearButton?: boolean
  isDisabled?: boolean
  onChange: (id: string) => void
  onClear?: () => void
  onGetCustomScript?: (data: GetCustomScriptList) => void
  value?: string
  placeholder?: string
  placement?: Placement
  tooltip?: string
}

const getOptions = (data: GetCustomScriptList) =>
  data.items.map(({
    id,
    name,
    is_default
  }) => ({
    label: name,
    value: id,
    isDefault: is_default
  }))

const SelectCustomScript = ({
  hasClearButton,
  isDisabled = false,
  onChange,
  onClear,
  onGetCustomScript = () => {},
  value,
  placeholder,
  placement,
  tooltip
}: Props) => {
  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const defaultTooltip = currentOption?.value
      ? `Custom script: ${currentOption.label}`
      : label

    const buttonTooltip = tooltip || defaultTooltip

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        isDisabled={isDisabled}
        label={label}
        placement={placement}
        title={buttonTooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  return (
    <GetCustomScriptListRequest shortErrorMessage='Failed to load Custom Script'>
      {
        (data: GetCustomScriptList) => {
          onGetCustomScript(data)

          if (data.total_count === 0) {
            return (
              <Paragraph>
                <Color variant='theme-description-text'>
                  No custom scripts available.
                </Color>
              </Paragraph>
            )
          }

          const options = getOptions(data)

          return (
            <Combobox
              hasClearButton={hasClearButton}
              onChange={onChange}
              onClear={onClear}
              options={options}
              placement={placement}
              renderButton={renderButton(options)}
              value={value}
            />
          )
        }
      }
    </GetCustomScriptListRequest>
  )
}

export default SelectCustomScript
